import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Link,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { useRef } from "react";

export default function RequestPartnershipPermissionModal({
  isOpen,
  partnerLabel,
  facebookPageOnly,
  isLoading,
  errorMessage,
  partnershipAdsHubUrl,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  partnerLabel: string;
  /** Partner selected by Facebook Page without a known Instagram id/username. */
  facebookPageOnly?: boolean;
  isLoading: boolean;
  errorMessage?: string | null;
  /** Deep link to Meta Partnership Ads Hub when API access is blocked. */
  partnershipAdsHubUrl?: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={() => {
        if (!isLoading) {
          onClose();
        }
      }}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Request partnership permission
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text fontSize="sm" mb={3}>
              Send a partnership ad permission request to{" "}
              <Text as="span" fontWeight="semibold">
                {partnerLabel}
              </Text>
              .
            </Text>
            <Text fontSize="sm" fontWeight="semibold" mb={1}>
              What happens next
            </Text>
            <UnorderedList fontSize="sm" spacing={1} pl={4} mb={3}>
              <ListItem>Meta notifies your partner on Instagram and/or Facebook.</ListItem>
              <ListItem>
                They approve in the Instagram or Facebook app — not in this
                dashboard.
              </ListItem>
              <ListItem>
                After you send, copy an approval link for them (shown below) or
                track the request in Partnership Ads Hub.
              </ListItem>
            </UnorderedList>
            {facebookPageOnly ? (
              <Text fontSize="sm" color="gray.600" mb={3}>
                You selected this partner by Facebook Page only. Meta sends the
                request at the Facebook Page level when the partner has no linked
                Instagram account.
              </Text>
            ) : null}
            {errorMessage ? (
              <Box
                p={2}
                borderRadius="md"
                bg="red.50"
                borderWidth="1px"
                borderColor="red.200"
              >
                <Text fontSize="sm" fontWeight="semibold" color="red.800">
                  Could not send partnership request
                </Text>
                <Text fontSize="sm" color="red.700" mt={1}>
                  {errorMessage}
                </Text>
                {partnershipAdsHubUrl ? (
                  <Text fontSize="sm" color="red.700" mt={2}>
                    Send the request manually in{" "}
                    <Link
                      href={partnershipAdsHubUrl}
                      isExternal
                      color="red.800"
                      fontWeight="semibold"
                      textDecoration="underline"
                    >
                      Partnership Ads Hub
                    </Link>
                    .
                  </Text>
                ) : null}
              </Box>
            ) : null}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              ml={3}
              isLoading={isLoading}
              onClick={onConfirm}
            >
              Send request
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
